import React, { useState } from 'react';
import { Box, TextField, Button, List, ListItem, ListItemText, IconButton, Typography, ListItemButton } from '@mui/material';
import { Delete as DeleteIcon, AttachFile as AttachFileIcon, Download as DownloadIcon } from '@mui/icons-material';
import { RowContainer, BaseLabel, cores } from '../../../styles/styleUtils';
import { downloadAnexo } from '../../api'; // Importa a nova função

// Helper para ler o arquivo como Base64
const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result); // Pega só o base64
    reader.onerror = error => reject(error);
});

// Helper para baixar arquivos Base64 (novos, ainda no front)
const downloadBase64File = (base64Data, filename, ext) => {
    const downloadLink = document.createElement("a");
    downloadLink.href = base64Data;
    downloadLink.download = `${filename}.${ext}`;
    downloadLink.click();
};


function OsAnexos({ anexos = [], onAddAnexo, onRemoveAnexo }) {
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [currentFile, setCurrentFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = React.useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCurrentFile(file);
      if (!nomeArquivo) {
        setNomeArquivo(file.name.split('.').slice(0, -1).join('.'));
      }
    }
  };

  const handleAnexar = async () => {
    if (!currentFile || !nomeArquivo) {
      alert("Por favor, selecione um arquivo e dê um nome a ele.");
      return;
    }

    setIsUploading(true);
    try {
      const base64 = await toBase64(currentFile);
      const ext = currentFile.name.split('.').pop();

      onAddAnexo({
        nome: nomeArquivo,
        ext,
        base64, // Anexos novos têm base64
        // Anexos novos NÃO têm _id
      });

      setNomeArquivo('');
      setCurrentFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Erro ao converter arquivo para Base64:", error);
      alert("Erro ao processar o arquivo.");
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * @description Lida com o clique no anexo para download
   */
  const handleDownload = async (anexo) => {
    const filename = `${anexo.nome}.${anexo.ext}`;

    if (anexo.base64) {
      // É um arquivo NOVO, ainda não salvo (download via Base64)
      downloadBase64File(anexo.base64, anexo.nome, anexo.ext);
    } else if (anexo._id) {
      // É um arquivo EXISTENTE no servidor (download via API)
      try {
        await downloadAnexo(anexo._id, filename);
      } catch (error) {
        alert("Erro ao baixar o anexo. Tente novamente.");
      }
    } else {
      console.warn("Anexo em estado inválido (sem base64 ou _id):", anexo);
    }
  };

  return (
    <Box>
      <RowContainer>
        {/* Input de Nome do Arquivo */}
        <BaseLabel htmlFor="nomeArquivo" style={{ flex: 1 }}>Nome do Arquivo</BaseLabel>
        <TextField
          id="nomeArquivo"
          name="nomeArquivo"
          value={nomeArquivo}
          onChange={(e) => setNomeArquivo(e.target.value)}
          placeholder="Ex: Nota Fiscal Inversor"
          sx={{ flex: 3, mr: 2, backgroundColor: cores.branco}}
          disabled={isUploading}
        />

        {/* Botão "Anexar arquivo" que clica no input escondido */}
        <Button
          variant="contained"
          // component="label" // <--- REMOVIDO: Esta era a causa do clique duplo
          startIcon={<AttachFileIcon />}
          onClick={() => fileInputRef.current && fileInputRef.current.click()} // Usamos apenas o onClick programático
          sx={{ flex: 1.5, mr: 2, backgroundColor: cores.laranja, '&:hover': { backgroundColor: cores.laranjaEscuro } }}
          disabled={isUploading}
        >
          {currentFile ? (currentFile.name.length > 15 ? `${currentFile.name.substring(0, 12)}...`: currentFile.name) : "Selecionar..."}
          <input
            type="file"
            ref={fileInputRef}
            hidden
            onChange={handleFileSelect}
          />
        </Button>

        {/* Botão "Adicionar Anexo" */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleAnexar}
          sx={{ flex: 1.5 }}
          disabled={isUploading || !currentFile || !nomeArquivo}
        >
          {isUploading ? "Enviando..." : "Adicionar Anexo"}
        </Button>
      </RowContainer>

      {/* Lista de Anexos Adicionados */}
      <List dense>
        {anexos.length === 0 && (
          <Typography sx={{ color: cores.textoClaro, fontStyle: 'italic', ml: 2 }}>
            Nenhum anexo adicionado.
          </Typography>
        )}
        {anexos.map((anexo, index) => (
          // Usamos ListItemButton para dar o efeito de clique
          <ListItemButton
            key={anexo._id || index} // Usa _id se existir, senão o index
            sx={{ backgroundColor: 'rgba(255,255,255,0.1)', mb: 1, borderRadius: '4px' }}
            onClick={() => handleDownload(anexo)}
          >
            <DownloadIcon sx={{ color: cores.textoClaro, mr: 2 }} />
            <ListItemText
              primary={`${anexo.nome}.${anexo.ext}`}
              primaryTypographyProps={{ color: cores.textoClaro }}
            />
            {/* O botão de delete fica fora da área de clique do download */}
            <IconButton
              edge="end"
              aria-label="delete"
              onClick={(e) => {
                e.stopPropagation(); // Impede que o clique acione o download
                onRemoveAnexo(index);
              }}
              sx={{ ml: 2 }}
            >
              <DeleteIcon sx={{ color: '#dc3545' }} />
            </IconButton>
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
}

export default OsAnexos;